import {
  allowedValuesRule,
  asWarning,
  cannotClearRule,
  changeFlagFill,
  changeFlagRule,
  exactLengthRule,
  itemPrefixRule,
  maxAmountRule,
  noEffectRule,
  notInUseRule,
  notLoadedRule,
  onlyIn,
  requiredFlagRule,
  requiredRule,
  reservedPositionsRule,
  type CellRule,
  type WorksheetRules,
} from '../as400CellRuleKinds';
import {
  itemAddHasLanguageSlots,
  itemAddRequiredRule,
  SHELF_LIFE_LANGUAGES,
} from '../itemAddRules';
import {
  expectedPricingFactor,
  pricingFactorMatches,
} from './sellUomPricingFactors';

// Change rows' paired change flags: any edit of the target fills its flag.
const CHANGE_FLAGS = [
  // Feedback 2026-10-22: PROD SEQ # loads only with UPDATE PROD SEQ # Y.
  ['IPRDSQ', 'UPDATE_IPRDSQ'],
  ['IQTEU1', 'CHG_IQTEU1'],
  ['IQTEU2', 'CHG_IQTEU2'],
  ['IQTEU3', 'CHG_IQTEU3'],
  ['IQTFU1', 'CHG_IQTFU1'],
  ['IQTFU2', 'CHG_IQTFU2'],
  ['IQTFU3', 'CHG_IQTFU3'],
] as const;

// PROD REC STATUS and CHANGE STATUS FLAG: paired the same way, and filled on
// an add too (GEN-2734).
const STATUS_FLAG = ['ISTAT', 'CHANGE_STATUS_FLAG'] as const;

export const BUYER_COST_VL_THRESHOLD = 85000;

function sellUomPricingFactorRule(): CellRule {
  return {
    field: 'IPPRQT',
    dependsOn: ['ISLUOM'],
    check(value, row) {
      const sellUom = row.value('ISLUOM');
      const expected = expectedPricingFactor(sellUom);
      if (expected === null || pricingFactorMatches(value, expected)) {
        return null;
      }
      return {
        rule: 'pricing-factor',
        expected,
        sellUom: sellUom ?? '',
      };
    },
  };
}

/**
 * The Item worksheet's own rules, on top of its columns' character limits and
 * header masks (SHP'G MULT 99999 and CTN QTY 9999 are whole numbers, GEN-2725;
 * every other mask is read the same way, GEN-2726). Append new entries; keep
 * one ticket's entries together under its comment.
 */
export const ITEM_SHEET_RULES: WorksheetRules = {
  rules: [
    // GEN-2724: a tariff code is two characters, kept as text so a leading
    // zero survives. One character is a slip, flagged when the cell is left.
    exactLengthRule('ITARIF', 2),
    // GEN-2737: nobody loads core cost yet. The mask (99999.9999, or -1 to
    // clear) is the column's; any value also asks for a second look until
    // Aisha confirms the field is in use.
    asWarning(notInUseRule('ICCST', 'Aisha')),
    // GEN-2734: PROD REC STATUS is a capital S (suspend) or blank, and either
    // takes effect only with CHANGE STATUS FLAG Y — blank + Y unsuspends.
    // Any edit fills the flag (below), and on a change row the flag is paired
    // with the field like the others (CHANGE_FLAGS); an add's S still needs it.
    // Nobody has used N in 20 years: accepted, but it does nothing.
    allowedValuesRule('ISTAT', ['S'], { blankAllowed: true }),
    allowedValuesRule('CHANGE_STATUS_FLAG', ['Y', 'N'], { blankAllowed: true }),
    onlyIn(
      'add',
      requiredFlagRule(
        'CHANGE_STATUS_FLAG',
        { field: 'ISTAT', value: 'S' },
        'Y',
      ),
    ),
    asWarning(noEffectRule('CHANGE_STATUS_FLAG', 'N', ['Y'])),
    // GEN-2738: Prd Type is F (finished good), T or W (templates), or blank
    // on a change. An add always gets one — F unless the author picks a
    // template on purpose (the add default, below).
    onlyIn(
      'change',
      allowedValuesRule('ITYPE', ['T', 'W', 'F'], {
        blankAllowed: true,
      }),
    ),
    onlyIn('add', allowedValuesRule('ITYPE', ['T', 'W', 'F'])),
    // GEN-2736: Item Category 5's first four characters are the eco fee; the
    // AS/400 writes the fifth from Product Serialization Flag, ignoring what
    // is typed there (a `/////` blank-out still spans the whole field). The
    // flag is mandatory on an add, defaults to N, and only ALB items are
    // serialized today — a Y elsewhere is worth a second look. (Requiring
    // approval for a Y waits on GEN-2728.)
    reservedPositionsRule('ICAT5', 4, 'SERIALIZATION_FLAG'),
    onlyIn(
      'change',
      allowedValuesRule('SERIALIZATION_FLAG', ['Y', 'N'], {
        blankAllowed: true,
      }),
    ),
    onlyIn('add', allowedValuesRule('SERIALIZATION_FLAG', ['Y', 'N'])),
    asWarning(
      itemPrefixRule('SERIALIZATION_FLAG', 'Y', {
        itemField: 'ITEMNO',
        prefix: 'ALB',
      }),
    ),
    itemAddRequiredRule('HAZMAT_FLAG', { itemField: 'ITEMNO' }),
    itemAddRequiredRule('SHELF_LIFE_FLAG', {
      itemField: 'ITEMNO',
      languages: itemAddHasLanguageSlots() ? SHELF_LIFE_LANGUAGES : undefined,
    }),
    ...[...CHANGE_FLAGS, STATUS_FLAG].map(([target, flag]) =>
      onlyIn('change', changeFlagRule(flag, target)),
    ),
    maxAmountRule('IBCST', BUYER_COST_VL_THRESHOLD),
    asWarning(notLoadedRule('ILCST')),
    sellUomPricingFactorRule(),
    cannotClearRule('IALPH1'),
    onlyIn('add', requiredRule('IALPH1')),
  ],
  autoFills: [
    // GEN-2734: any edit of PROD REC STATUS fills its flag, an add's S
    // included (an add's blank fills nothing).
    changeFlagFill(STATUS_FLAG[1], STATUS_FLAG[0]),
    ...CHANGE_FLAGS.map(([target, flag]) => ({
      ...changeFlagFill(flag, target),
      modes: ['change' as const],
    })),
  ],
  addDefaults: [
    // GEN-2738: nine adds in ten are finished goods.
    { field: 'ITYPE', value: () => 'F' },
    // GEN-2736: a Y is costly to undo; it has to be chosen.
    { field: 'SERIALIZATION_FLAG', value: () => 'N' },
  ],
};
